import React from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import { Button, Paper, Typography, Box } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import { toast } from "react-toastify";

export default function MyProfile() {
  const { t } = useTranslation();
  const userStore = useStore((s) => s.userStore);

  const initialValues: ChangePasswordFormValues = {
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  };

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required(t("myProfile.currentPasswordRequired")),
    newPassword: Yup.string()
      .required(t("myProfile.newPasswordRequired"))
      .min(8, t("myProfile.newPasswordMinLength"))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/, t("myProfile.newPasswordComplexity")),
    confirmNewPassword: Yup.string()
      .required(t("myProfile.confirmNewPasswordRequired"))
      .oneOf([Yup.ref("newPassword")], t("myProfile.passwordsDoNotMatch")),
  });

  const handlePasswordChange = async (values: ChangePasswordFormValues, resetForm: any) => {
    try {
      await userStore.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.confirmNewPassword,
      });
      resetForm();
      toast.success(t("myProfile.passwordChangedSuccess"));
    } catch {
      toast.error(t("myProfile.invalidCurrentPassword"));
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">
          {t("myProfile.username")} - {userStore.loggedUser?.username}
        </Typography>
      </Box>
      <Paper sx={{ p: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }: any) => handlePasswordChange(values, resetForm)}
          enableReinitialize={true}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("myProfile.changePassword")}
              </Typography>
              <CustomTextInput
                type="password"
                name="currentPassword"
                placeholder={t("myProfile.currentPassword")}
                label={t("myProfile.currentPassword")}
              />
              <CustomTextInput
                type="password"
                name="newPassword"
                placeholder={t("myProfile.newPassword")}
                label={t("myProfile.newPassword")}
              />
              <CustomTextInput
                type="password"
                name="confirmNewPassword"
                placeholder={t("myProfile.confirmNewPassword")}
                label={t("myProfile.confirmNewPassword")}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty || !isValid}
                type="submit"
                sx={{ float: "right", mt: 2 }}
              >
                {t("myProfile.changePassword")}
              </Button>
            </form>
          )}
        </Formik>
      </Paper>
    </>
  );
}

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}
